import { useState, useEffect, Dispatch, SetStateAction, useMemo } from "react";
import { Header } from "../../components/Header";
import { Stepper } from "../../components/Stepper";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  IconButton,
  IconButtonSize,
  Input,
  InputSize,
  Show
} from "shared/components";
import { ApplicantType } from "shared/types";
import { toast } from "react-toastify";
import { ManualOption } from "../../types";
import { SelfServeOption } from "../../types";
import { RoleSelect } from "./components/RoleSelect";
import { validateForms } from "./validations";
import { useGetInviteTemplateAndUnit } from "./hooks";
import { useDiscounts } from "views/discounts/hooks/useDiscounts";
import { DiscountCode, CustomDiscount } from "views/discounts/types";
import addressApi from "api/landlord/address";
import { useParams } from "react-router-dom";
import { ActiveDiscountsModal } from "views/discounts/components/ActiveDiscountsModal";
import { ApplyDiscountModal } from "views/discounts/components/ApplyDiscountModal";
import { formatToCurrency } from "shared/utils";
import { useQuery } from "@tanstack/react-query";
import {
  Delete01Icon,
  DiscountTag02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { InvitationRadioType } from "./components/InvitationRadioType";
import { DiscountActionCell } from "./components/DiscountActionCell";
import { Property } from "types/landlord/address";

// Yunes said this is gonna be different feature
const isApplyDiscountInTableAvailable = false;
import { Template } from "types/landlord/verificationTemplate";

const BLANK_SELF_SERVE_OPTION: SelfServeOption = {
  type: "self-serve",
  email: "",
  first_name: "",
  last_name: "",
  role: ApplicantType.Applicant
};

const BLANK_MANUAL_OPTION: ManualOption = {
  type: "manual",
  first_name: "",
  last_name: "",
  role: ApplicantType.Applicant
};

export const InviteApplicants = ({
  setOpen,
  handleInvite,
  isInviteLoading,
  applicationId,
  propertyId,
  unitId,
  selectedTemplate,
  disabledRoles: _disabledRoles,
  unitSelectSkipped
  // setStepIdx
}: {
  setOpen: (open: boolean) => void;
  handleInvite: (
    options: (SelfServeOption | ManualOption)[],
    templateId?: string
  ) => void;
  isInviteLoading: boolean;
  unitSelectSkipped: boolean;
  applicationId?: number;
  propertyId?: number;
  unitId?: number;
  selectedTemplate?: Template;
  disabledRoles?: ApplicantType[];
  setStepIdx?: Dispatch<SetStateAction<number>>;
}) => {
  const { templateVerificationsSettings, applicationMeta } =
    useGetInviteTemplateAndUnit({
      applicationId,
      propertyId,
      unitId
    });
  const { propertyId: urlPropertyId, unitId: urlUnitId } = useParams();
  const [optionToAdd, setOptionToAdd] = useState<
    SelfServeOption | ManualOption
  >(BLANK_SELF_SERVE_OPTION);

  const [templateToUse, setTemplateToUse] = useState<Template | undefined>(
    selectedTemplate
  );

  const [
    templateVerificationsSettingsToUse,
    setTemplateVerificationsSettingsToUse
  ] = useState(templateVerificationsSettings);

  const [options, setOptions] = useState<(ManualOption | SelfServeOption)[]>(
    []
  );
  const [showDiscountsModal, setShowDiscountsModal] = useState(false);
  const [appliedDiscounts, setAppliedDiscounts] = useState<{
    [key: string]: DiscountCode | CustomDiscount | null;
  }>({});

  const [showApplyDiscountModal, setShowApplyDiscountModal] = useState(false);
  const [selectedApplicantEmail, setSelectedApplicantEmail] =
    useState<string>("");

  const handleDiscountApplication = (
    discount: DiscountCode | CustomDiscount
  ) => {
    const targetOption = options.find((option) => {
      const email =
        option.type === "self-serve"
          ? option.email
          : `${option.first_name} ${option.last_name}`;
      return email === selectedApplicantEmail;
    });

    if (targetOption) {
      const targetIdx = options.indexOf(targetOption);
      const rowKey =
        targetOption.type === "self-serve"
          ? targetOption.email
          : `${targetOption.first_name}_${targetOption.last_name}` ||
            `row_${targetIdx}`;

      setAppliedDiscounts({
        ...appliedDiscounts,
        [rowKey as string]: discount
      });
    }
    setShowApplyDiscountModal(false);
  };

  const propertyIdToUse = urlPropertyId || propertyId;
  const { data: property, isLoading: isPropertyLoading } = useQuery<Property>({
    queryKey: ["PROPERTY", propertyIdToUse],
    queryFn: () => addressApi.getProperty(String(propertyIdToUse!)),
    enabled: !!propertyIdToUse
  });

  // Determining the verification template to use
  // 1. Highest priority is the application's template
  // 2. Then the selected template (i.e. during application creation)
  // 3. Then the property's template as a fallback
  useEffect(() => {
    if (applicationMeta.data?.verification_template)
      setTemplateToUse(applicationMeta.data.verification_template);
    else if (selectedTemplate) setTemplateToUse(selectedTemplate);
    else setTemplateToUse(property?.verification_template);
  }, [
    applicationMeta.data?.verification_template,
    selectedTemplate,
    property?.verification_template
  ]);

  useEffect(() => {
    setTemplateVerificationsSettingsToUse(templateToUse?.settings);
  }, [templateToUse]);

  useQuery({
    queryKey: ["UNIT", urlUnitId],
    queryFn: () => addressApi.getUnitById(urlUnitId!),
    enabled: !!urlUnitId && !isNaN(parseInt(urlUnitId))
  });

  const { data: activeDiscountsData = [], isLoading } = useDiscounts({
    active: true,
    restriction_type: "template",
    template_id: templateToUse?.id,
    enabled: !!templateToUse?.id
  });

  const disabledRoles =
    _disabledRoles ||
    (options.some((option) =>
      [ApplicantType.Applicant, ApplicantType.Occupant].includes(option.role)
    )
      ? []
      : [ApplicantType.CoSigner]);

  // Delete applicant from list
  const handleDeleteApplicant = (idx: number, rowKey: string) => {
    const newOptions = options.filter((_, i) => i !== idx);
    setOptions(newOptions);

    // Also remove any applied discount for this option
    const updatedDiscounts = { ...appliedDiscounts };
    delete updatedDiscounts[rowKey];
    setAppliedDiscounts(updatedDiscounts);
  };

  const hasApplicants =
    applicationMeta.data?.applicants.some((appl) =>
      [
        ApplicantType.Occupant as string,
        ApplicantType.Applicant as string
      ].includes(appl.role || "")
    ) ||
    applicationMeta.data?.pending_magic_links?.some((pml) =>
      [ApplicantType.Applicant, ApplicantType.Occupant].includes(pml.role)
    );

  const hasTemplateCombinedIncome = useMemo(
    () =>
      Object.values(templateToUse?.verification_schemas || {}).some(
        (role) => role.combined_income_verification
      ),
    [templateToUse?.verification_schemas]
  );

  const canCreateManual = useFeatureFlagEnabled("manual-applications-enabled");

  const manualRadioClassName = useMemo(() => {
    if (isPropertyLoading) return "animate-pulse";
    if (hasTemplateCombinedIncome) return "opacity-[60%]";

    return "";
  }, [hasTemplateCombinedIncome, isPropertyLoading]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <Header
        title="Add applicants"
        description="You can invite several people to the unit"
        onClose={() => setOpen(false)}
      />

      <div className="flex-1 overflow-y-auto min-h-0">
        <Hide if={unitSelectSkipped}>
          <div className="p-6 border-b border-alpha-40">
            <Stepper currentStepIdx={1} totalSteps={2} />
          </div>
        </Hide>
        <div className="space-y-5 p-6">
          <InvitationRadioType
            type="self-serve"
            title="Self-serve"
            description="Invite a person via email to complete the application"
            checked={optionToAdd.type === "self-serve"}
            onChange={() => setOptionToAdd(BLANK_SELF_SERVE_OPTION)}
          />
          <Show if={canCreateManual}>
            <div className={manualRadioClassName}>
              <InvitationRadioType
                type="manual"
                title="Manual"
                disabled={isPropertyLoading || hasTemplateCombinedIncome}
                description="Manually fill in the person's information to run the screening services"
                checked={optionToAdd.type === "manual"}
                onChange={() => setOptionToAdd(BLANK_MANUAL_OPTION)}
              />
            </div>
          </Show>

          <form
            onSubmit={(e) => {
              e.preventDefault();

              const errors = validateForms(optionToAdd, options);
              if (errors) {
                toast.error(errors);
                return;
              }

              setOptions([...options, optionToAdd]);
              e.currentTarget.reset();

              setOptionToAdd((prev) =>
                prev.type === "self-serve"
                  ? BLANK_SELF_SERVE_OPTION
                  : BLANK_MANUAL_OPTION
              );
            }}
          >
            <legend className="text-sm text-content-700 font-medium mb-2">
              Add person {optionToAdd.type === "self-serve" ? "by email" : ""}
            </legend>

            <div className="flex gap-4">
              {optionToAdd.type === "self-serve" ? (
                <div className="flex gap-4">
                  <div className="w-full max-w-[300px]">
                    <Input
                      placeholder="Enter email"
                      value={optionToAdd.email || ""}
                      onChange={(e) =>
                        setOptionToAdd({
                          ...optionToAdd,
                          email: e.target.value
                        })
                      }
                      id="email"
                    />
                  </div>

                  <div className="-mt-7 text-sm text-content-700 font-medium">
                    <legend>
                      Full name{" "}
                      <span className="text-content-100">(optional)</span>
                    </legend>

                    <div className="flex w-full mt-2">
                      <div className="w-full">
                        <Input
                          className="rounded-r-none rounded-l-md"
                          id="first_name"
                          placeholder="Enter first name"
                          value={optionToAdd.first_name || ""}
                          onChange={(e) =>
                            setOptionToAdd({
                              ...optionToAdd,
                              first_name: e.target.value
                            })
                          }
                        />
                      </div>
                      <div className="w-full">
                        <Input
                          className="rounded-l-none rounded-r-md border-l-0"
                          id="last_name"
                          placeholder="Enter last name"
                          value={optionToAdd.last_name || ""}
                          onChange={(e) =>
                            setOptionToAdd({
                              ...optionToAdd,
                              last_name: e.target.value
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex w-full">
                  <div className="w-full">
                    <Input
                      className="rounded-r-none rounded-l-lg"
                      id="first_name"
                      placeholder="Enter first name"
                      value={optionToAdd.first_name || ""}
                      onChange={(e) =>
                        setOptionToAdd({
                          ...optionToAdd,
                          first_name: e.target.value
                        })
                      }
                    />
                  </div>
                  <div className="w-full">
                    <Input
                      className="rounded-l-none rounded-r-lg border-l-0"
                      id="last_name"
                      placeholder="Enter last name"
                      value={optionToAdd.last_name || ""}
                      onChange={(e) =>
                        setOptionToAdd({
                          ...optionToAdd,
                          last_name: e.target.value
                        })
                      }
                    />
                  </div>
                </div>
              )}

              <div>
                <RoleSelect
                  className="w-[142px]"
                  value={optionToAdd.role}
                  onChange={(role) => setOptionToAdd({ ...optionToAdd, role })}
                  isDisabled={false}
                  disabledRoles={disabledRoles}
                  templateVerificationsSettings={
                    templateVerificationsSettingsToUse
                  }
                />
              </div>

              <Button
                type="submit"
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.BASE}
                className="shrink-0"
              >
                Add
              </Button>
            </div>
          </form>

          {options.length ? (
            <div className="w-full text-sm">
              {/* Header row */}
              <div className="grid grid-cols-[290px_140px_1fr_auto] gap-x-4 border-b border-alpha-40 text-xs font-medium text-content-300 pb-3.5">
                <div>Added people</div>
                <div>Role</div>
                <div className="text-right">Application fee</div>
                <Show if={isApplyDiscountInTableAvailable}>
                  <div className="text-center">Apply discount</div>
                </Show>
              </div>

              {/* Body */}
              <div className="divide-y divide-alpha-40">
                {options.map((option, idx) => {
                  const rowKey =
                    (option.type === "self-serve"
                      ? option.email
                      : `${option.first_name}_${option.last_name}`) ||
                    `row_${idx}`;

                  const disabledRowRoles = _disabledRoles || [];

                  if (
                    !disabledRoles.includes(ApplicantType.CoSigner) &&
                    !options.some(
                      (option, cIdx) =>
                        [
                          ApplicantType.Applicant,
                          ApplicantType.Occupant
                        ].includes(option.role) && cIdx !== idx
                    )
                  )
                    disabledRowRoles.push(ApplicantType.CoSigner);

                  return (
                    <div
                      key={rowKey}
                      className="grid grid-cols-[290px_140px_1fr_auto] gap-x-4 py-3 items-center"
                    >
                      {/* Person */}
                      <div className="flex items-center gap-4">
                        <IconButton
                          className="shrink-0"
                          HugeIcon={Delete01Icon}
                          size={IconButtonSize.SM}
                          onClick={() => handleDeleteApplicant(idx, rowKey)}
                        />
                        <div className="truncate">
                          <p className="font-medium text-sm text-content-1000 truncate mb-2">
                            {option.type === "self-serve"
                              ? option.email
                              : `${option.first_name} ${option.last_name}`}
                          </p>

                          <p className="text-xs text-content-400">
                            {option.type === "self-serve"
                              ? "Self-serve application"
                              : "Manual application"}
                          </p>
                        </div>
                      </div>
                      {/* Role */}
                      <div>
                        <RoleSelect
                          className="w-full !mb-0"
                          size={InputSize.SM}
                          value={option.role}
                          onChange={(role) => {
                            const updated = { ...option, role };
                            setOptions(
                              options.map((o, i) => (i === idx ? updated : o))
                            );
                          }}
                          isDisabled={false}
                          disabledRoles={disabledRowRoles}
                          templateVerificationsSettings={
                            templateVerificationsSettingsToUse
                          }
                        />
                      </div>
                      {/* Fee */}
                      <div className="font-bold text-right text-content-1000">
                        {formatToCurrency(
                          {
                            cents:
                              templateToUse?.[`${option.role}_fee`]?.cents ?? 0,
                            currency: "USD"
                          },
                          { maximumFractionDigits: 2, minimumFractionDigits: 2 }
                        )}
                      </div>
                      {/* Discount */}
                      <Show if={isApplyDiscountInTableAvailable}>
                        <div className="text-center">
                          <DiscountActionCell
                            appliedDiscounts={appliedDiscounts}
                            rowKey={rowKey}
                            propertyApplicationFee={
                              templateToUse?.[`${option.role}_fee`]?.cents
                            }
                            template={{ verification_template: templateToUse }}
                            setAppliedDiscounts={setAppliedDiscounts}
                            option={option}
                            setSelectedApplicantEmail={
                              setSelectedApplicantEmail
                            }
                            setShowApplyDiscountModal={
                              setShowApplyDiscountModal
                            }
                          />
                        </div>
                      </Show>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="px-6 py-4 border-t border-alpha-40">
        <div className="flex justify-between">
          <Show if={activeDiscountsData.length > 0}>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.XS}
              HugeIcon={DiscountTag02Icon}
              disabled={!activeDiscountsData.length || isLoading}
              className="flex items-center gap-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDiscountsModal(true);
              }}
            >
              View active discount codes
            </Button>
          </Show>
          <div className="flex gap-2 ml-auto">
            {/* Hiding this BACK BUTTON for now. Think this is bad regression
             * this causes a bug where if you click back, it will
             * not prefill property and unit
             */}

            {/* <Button
              variant={ButtonVariant.BASE}
              onClick={() => setStepIdx?.(0)}
              disabled={!setStepIdx}
            >
              Back
            </Button> */}
            <Button
              className="min-w-[150px]"
              variant={ButtonVariant.PRIMARY}
              disabled={
                !options.length ||
                (options.every((o) => o.role === ApplicantType.CoSigner) &&
                  !hasApplicants)
              }
              tooltipZIndex={50}
              tooltip={
                options.length &&
                !hasApplicants &&
                options.every((o) => o.role === ApplicantType.CoSigner)
                  ? "Cannot create applications with only Guarantors"
                  : undefined
              }
              onClick={() => handleInvite(options, selectedTemplate?.id)}
              loading={isInviteLoading || applicationMeta.isLoading}
            >
              Create application
            </Button>
          </div>
        </div>
      </div>

      <ActiveDiscountsModal
        isOpen={showDiscountsModal}
        onClose={() => setShowDiscountsModal(false)}
        discounts={activeDiscountsData}
      />

      <ApplyDiscountModal
        opened={showApplyDiscountModal}
        onClose={() => setShowApplyDiscountModal(false)}
        applicantEmail={selectedApplicantEmail}
        activeDiscounts={activeDiscountsData}
        onApplyDiscount={handleDiscountApplication}
      />
    </div>
  );
};
